import type { ScreenWakePort } from '@Domain/Ports/ScreenWake/ScreenWakePort';

/** The slice of `Document` this adapter needs to detect a return to the foreground. */
type VisibilityEventTarget = {
	addEventListener(type: 'visibilitychange', listener: () => void): void;
	removeEventListener(type: 'visibilitychange', listener: () => void): void;
	readonly hidden: boolean;
};

/**
 * Holds a Screen Wake Lock sentinel for as long as the caller wants the screen kept on.
 *
 * `navigator.wakeLock` is undefined under jsdom and absent from insecure contexts and some
 * browsers, so it is received by the constructor rather than read from the global at call time —
 * `wakeLock` is typed `WakeLock | undefined` on purpose, so a caller can construct this adapter
 * with no lock support at all without any global polyfill.
 *
 * The platform silently drops the lock as soon as the page leaves the foreground: this is the
 * one behaviour a test cannot get from the real `WakeLockSentinel` (jsdom has none), so recovery
 * is driven entirely by the injected `visibilityTarget`, never by the sentinel itself. `released`
 * tracks whether the *caller* asked for the lock to be given up — reacquiring on return to the
 * foreground only happens while that is false.
 */
class WebScreenWakeAdapter implements ScreenWakePort {
	private sentinel: WakeLockSentinel | null = null;
	private releasedByCaller = true;
	private acquiring = false;
	private visibilitySubscribed = false;

	private readonly heldListeners = new Set<(isHeld: boolean) => void>();

	constructor(
		private readonly wakeLock: WakeLock | undefined = navigator.wakeLock,
		private readonly visibilityTarget: VisibilityEventTarget = document,
	) {}

	isSupported(): boolean {
		return this.wakeLock !== undefined;
	}

	async acquire(): Promise<void> {
		this.subscribeToVisibility();
		this.releasedByCaller = false;
		await this.requestSentinel();
	}

	async release(): Promise<void> {
		this.releasedByCaller = true;
		await this.releaseSentinel();
		this.unsubscribeFromVisibility();
	}

	isHeld(): boolean {
		return this.sentinel !== null && !this.sentinel.released;
	}

	subscribe(listener: (isHeld: boolean) => void): () => void {
		this.heldListeners.add(listener);
		return () => {
			this.heldListeners.delete(listener);
		};
	}

	private readonly handleVisibilityChange = (): void => {
		if (this.visibilityTarget.hidden) {
			return;
		}
		if (this.releasedByCaller) {
			return;
		}
		if (this.sentinel !== null && !this.sentinel.released) {
			return;
		}
		void this.requestSentinel();
	};

	private async requestSentinel(): Promise<void> {
		if (this.wakeLock === undefined || this.acquiring) {
			return;
		}
		if (this.sentinel !== null && !this.sentinel.released) {
			return;
		}
		this.acquiring = true;
		try {
			const sentinel = await this.wakeLock.request('screen');
			sentinel.onrelease = () => {
				this.sentinel = null;
				this.notifyHeldChange();
			};
			this.sentinel = sentinel;
			this.notifyHeldChange();
		} catch {
			// The platform can refuse a request (low battery, backgrounded already, not a secure
			// context): a wake lock is a best-effort mitigation, never a guarantee the caller can
			// rely on, so this never throws back to it.
			this.sentinel = null;
		} finally {
			this.acquiring = false;
		}
	}

	private async releaseSentinel(): Promise<void> {
		if (this.sentinel === null) {
			return;
		}
		const sentinel = this.sentinel;
		this.sentinel = null;
		try {
			await sentinel.release();
		} finally {
			this.notifyHeldChange();
		}
	}

	private notifyHeldChange(): void {
		const isHeld = this.isHeld();
		this.heldListeners.forEach((listener) => {
			listener(isHeld);
		});
	}

	/** Visibility only matters while the caller actually wants the lock held: subscribing outside
	 * `acquire()`/`release()` would fire `handleVisibilityChange` for a caller that never asked for
	 * anything. Both methods are idempotent on `visibilitySubscribed`, so repeated `acquire()`/
	 * `release()` pairs never double-subscribe and never call `removeEventListener` without a
	 * matching subscription. */
	private subscribeToVisibility(): void {
		if (this.visibilitySubscribed) {
			return;
		}
		this.visibilityTarget.addEventListener('visibilitychange', this.handleVisibilityChange);
		this.visibilitySubscribed = true;
	}

	private unsubscribeFromVisibility(): void {
		if (!this.visibilitySubscribed) {
			return;
		}
		this.visibilityTarget.removeEventListener('visibilitychange', this.handleVisibilityChange);
		this.visibilitySubscribed = false;
	}
}

export default WebScreenWakeAdapter;
export type { VisibilityEventTarget };
